import React, { useMemo, useState } from 'react'
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom'
import { login } from '../services/auth'
import { getUser } from '../services/helpers'
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import Logo from '../asset/images/logo.svg'

const Login = ({ defaultRole = 'ATTENDEE' }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Automatically determine role based on path; default to attendee otherwise
  const resolvedRole = location.pathname.includes('/center') ? 'CENTER' : defaultRole
  const isCenter = resolvedRole === 'CENTER'

  const verificationNotice = useMemo(() => {
    const verify = searchParams.get('verify')
    if (verify === '1' || verify === 'true') {
      return 'A verification link has been sent to your email. Please verify your email before logging in.'
    }
    const registered = searchParams.get('registered')
    if (registered === 'true' || registered === '1') {
      return 'Account created successfully. Please log in.'
    }
    return ''
  }, [searchParams])

  const [form, setForm] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  const onSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const res = await login({ ...form, role: resolvedRole })
    setLoading(false)
    if (res.status === 200) {
      const user = getUser()
      if (user && user.role) {
        // Check for pending purchase (redirect back to event after login)
        const pendingPurchase = sessionStorage.getItem('pendingPurchase')
        if (pendingPurchase && user.role === 'ATTENDEE') {
          try {
            const purchase = JSON.parse(pendingPurchase)
            sessionStorage.removeItem('pendingPurchase')
            navigate(`/events/${purchase.eventId}`)
            return
          } catch (err) {
            console.error('Error parsing pending purchase:', err)
          }
        }
        
        // Check for redirect state from location
        const from = location.state?.from
        if (from && user.role === 'ATTENDEE') {
          navigate(from)
          return
        }
        
        if (user.role === 'CENTER') {
          navigate('/center')
        } else {
          navigate('/account')
        }
      } else {
        setError('Failed to save session. Please try again.')
      }
    } else {
      setError(res.error || 'Login failed')
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gradient-to-br from-stone-50 via-amber-50/30 to-emerald-50/40 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center gap-1.5 mb-8 group">
          <img
            src={Logo}
            alt="Obule"
            className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
          />
          <span className="text-3xl font-semibold tracking-wide text-slate-900">
            bule
          </span>
        </Link>
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900">
            Welcome back
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Or{' '}
            <Link to={isCenter ? "/center/signup" : "/signup"} className="font-semibold text-emerald-600 hover:text-emerald-500 transition-colors">
              create a new {isCenter ? 'event center' : 'attendee'} account
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-10 shadow-xl border border-slate-100 sm:rounded-2xl sm:px-12">
          <form className="space-y-6" onSubmit={onSubmit}>
            {verificationNotice && (
              <div className="rounded-lg bg-emerald-50 p-4 border border-emerald-100 flex items-start gap-3">
                <div className="flex-shrink-0 text-emerald-500">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="text-sm text-emerald-800 font-medium">{verificationNotice}</div>
              </div>
            )}
            {error && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-100 flex items-start gap-3">
                <div className="flex-shrink-0 text-red-400">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" /></svg>
                </div>
                <div className="text-sm text-red-700 font-medium">{error}</div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-semibold leading-6 text-slate-900">
                Email address
              </label>
              <div className="relative mt-2">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <EnvelopeIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="name@example.com"
                  value={form.email}
                  onChange={onChange}
                  className="block w-full rounded-lg border-0 py-2.5 pl-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold leading-6 text-slate-900">
                Password
              </label>
              <div className="relative mt-2">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <LockClosedIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={form.password}
                  onChange={onChange}
                  className="block w-full rounded-lg border-0 py-2.5 pl-10 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm sm:leading-6 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/forgot-password" className="font-semibold text-emerald-600 hover:text-emerald-500">
                  Forgot password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center rounded-lg px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 transition-all disabled:opacity-70 disabled:cursor-not-allowed hover:shadow-md hover:-translate-y-0.5 transform duration-200"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                }}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-8 text-center text-xs text-slate-500">
            {!isCenter ? (
              <p>
                Are you an event organizer?{' '}
                <Link to="/center/login" className="font-semibold text-slate-900 hover:underline">
                  Log in to Organizer Dashboard
                </Link>
              </p>
            ) : (
              <p>
                Looking for tickets?{' '}
                <Link to="/login" className="font-semibold text-slate-900 hover:underline">
                  Log in as Attendee
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
